import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Formik, FormikValues } from "formik";
import { shareholderSchema } from "formValidations/ShareholderSchema";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import React from "react";
import { useParams } from "react-router-dom";
import {
  useCreateShareholderMutation,
  useUpdateShareholderMutation,
} from "store/apis/Shareholders";
import { Shareholder } from "types/Models/Shareholders";
import { BackendError } from "types/utils/error";
import { AddShareholderForm } from "./AddShareholderForm";
import {
  useGetEntityAPDetailsQuery,
  useUpdateEntityAPDetailsMutation,
} from "store/apis/directorsAndOfficers";

export const PERSONA = "PERSONA";
export const COMPANY_ENTITY = "COMPANY_ENTITY";

export const AddShareholder = ({
  isOpen,
  close,
  shareholder,
  onSuccess,
  entityId: entityIdFromProps,
  lockedShareholderType,
}: {
  isOpen: boolean;
  close: () => void;
  shareholder?: Shareholder | null;
  onSuccess?: (
    value: Shareholder,
    shareholderId: string | null,
    previousStake: string | null
  ) => void;
  entityId?: string;
  /** Skips the "Shareholder type" selector and locks the form to this type.
   * Only pass this when the type was already chosen by an earlier step
   * (e.g. AddPersonModal's "An entity" flow) — every other caller should
   * leave this unset so the user can pick Individual vs Entity themselves. */
  lockedShareholderType?: string;
}) => {
  const [addShareholder, { isLoading: isAdding }] =
    useCreateShareholderMutation();
  const [updateShareholder, { isLoading: isUpdating }] =
    useUpdateShareholderMutation();
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const { entityId } = useParams<{ entityId: string }>();
  const entity = entityId || entityIdFromProps;
  const [updateEntityAPDetails] = useUpdateEntityAPDetailsMutation();
  const { data: entityAPDetails } = useGetEntityAPDetailsQuery(
    { entityId },
    { skip: !entityId }
  );

  const handleCreateShareholder = async (values: FormikValues) => {
    try {
      const shareholder = await addShareholder({
        payload: {
          instance_id:
            values.shareholderType === PERSONA ? values.persona : values.entity,
          ownership_stake:
            values.ownership != null ? String(values.ownership) : "",
          shareholder_type: values.shareholderType,
          country_id: values.country_id,
          tin: values.tin,
          address_id: values.address,
          issued_common_stock:
            values.issuedCommonStock !== "" && values.issuedCommonStock != null
              ? Number(values.issuedCommonStock)
              : null,
          issued_preferred_stock:
            values.issuedPreferredStock !== "" &&
            values.issuedPreferredStock != null
              ? Number(values.issuedPreferredStock)
              : null,
          relationship_to_entity:
            values.shareholderType === COMPANY_ENTITY
              ? values.relationshipToEntity
              : null,
        },
        groupId,
        entityId: entity!,
      }).unwrap();
      if (entityAPDetails) {
        await updateEntityAPDetails({
          payload: { ...entityAPDetails, shareholder_exists: true },
          entityId,
        }).unwrap();
      }
      close();
      onSuccess?.(shareholder, null, "0");
      successToast({ message: "Shareholder added successfully" });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleEditShareholder = async (values: FormikValues) => {
    try {
      if (shareholder?.uuid) {
        const updatedShareholder = await updateShareholder({
          payload: {
            instance_id:
              values.shareholderType === PERSONA
                ? values.persona
                : values.entity,
            ownership_stake:
              values.ownership != null ? String(values.ownership) : "",
            shareholder_type: values.shareholderType,
            country_id: values.country_id,
            tin: values.tin,
            address_id: values.address,
            issued_common_stock:
              values.issuedCommonStock !== "" &&
              values.issuedCommonStock != null
                ? Number(values.issuedCommonStock)
                : null,
            issued_preferred_stock:
              values.issuedPreferredStock !== "" &&
              values.issuedPreferredStock != null
                ? Number(values.issuedPreferredStock)
                : null,
            relationship_to_entity:
              values.shareholderType === COMPANY_ENTITY
                ? values.relationshipToEntity
                : null,
          },
          groupId,
          shareholderId: shareholder.uuid,
        }).unwrap();
        onSuccess?.(
          updatedShareholder,
          shareholder.uuid,
          shareholder.ownership_stake
        );
        successToast({ message: "Shareholder updated successfully" });
      }
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const defaultValue = {
    shareholderType:
      shareholder?.shareholder_type || lockedShareholderType || "",
    persona: shareholder?.instance_id || "",
    entity: shareholder?.instance_id || "",
    tin: shareholder?.tin || "",
    ownership: shareholder?.ownership_stake || "",
    country_id: shareholder?.country?.uuid || "",
    address: shareholder?.address?.uuid || "",
    issuedCommonStock: shareholder?.issued_common_stock ?? "",
    issuedPreferredStock: shareholder?.issued_preferred_stock ?? "",
    relationshipToEntity: shareholder?.relationship_to_entity || "",
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content useCustomOverlay>
        <Formik
          initialValues={defaultValue}
          onSubmit={
            shareholder?.uuid ? handleEditShareholder : handleCreateShareholder
          }
          validationSchema={shareholderSchema}
          validateOnBlur={true}
          validateOnChange={true}
        >
          {({ submitForm }) => {
            return (
              <>
                <Modal.Header>
                  <Modal.Title>
                    {shareholder?.uuid ? "Edit" : "Add"} Shareholder
                  </Modal.Title>
                  <Modal.Close onClick={close} />
                </Modal.Header>
                <Modal.Body>
                  <AddShareholderForm
                    entityId={entity}
                    lockedShareholderType={lockedShareholderType}
                  />
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button type="button">Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    onClick={submitForm}
                    customType="primary"
                    type="submit"
                    isLoading={isAdding || isUpdating}
                    disabled={isAdding || isUpdating}
                  >
                    Save
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
